import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Alert } from '../../components/ui';
import { useAuth } from '../../hooks';
import { BackendApiError } from '../../services/backendApi';

function resolveDeleteAccountError(error: unknown): string {
  if (error instanceof BackendApiError) {
    if (error.status === 401) {
      return 'Your session has expired. Sign in again to continue.';
    }
    if (error.status === 409) {
      return 'This Passport account has already been deleted.';
    }
    if (error.message.trim()) {
      return error.message;
    }
  }

  return 'Could not delete your Passport account. Please try again.';
}

export const DeleteAccountLink: React.FC = () => (
  <div className="pt-2 text-center">
    <Link to="/account/delete" className="inline-flex flex-col items-center gap-1">
      <span className="text-sm font-semibold text-red-600 hover:text-red-700">
        Delete account
      </span>
      <span className="text-xs text-gray-500 max-w-sm">
        Permanently deletes your Passport account, including your profile, qualifications, documents, and shares.
      </span>
    </Link>
  </div>
);

export const DeleteAccount: React.FC = () => {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!acknowledged || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await deleteAccount();
      navigate('/account-deleted', { replace: true });
    } catch (err) {
      setError(resolveDeleteAccountError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Delete account</h1>
        <p className="mt-1 text-sm text-gray-500">
          Permanently delete your Passport account and the data stored in it.
        </p>
      </div>

      <Card>
        <CardBody>
          <p className="text-sm font-semibold text-gray-900 mb-3">What will be deleted</p>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              Your Passport profile, qualifications, work experience, documents, references, and shared links are permanently deleted.
            </p>
            <p>
              You will be signed out and will not be able to sign in to Passport with this account.
            </p>
            <p>This cannot be undone.</p>
          </div>
        </CardBody>
      </Card>

      <label className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 bg-white cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
          checked={acknowledged}
          onChange={event => setAcknowledged(event.target.checked)}
        />
        <span className="text-sm text-gray-800">
          I understand this permanently deletes my Passport account and cannot be undone
        </span>
      </label>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Button
        type="button"
        variant="danger"
        fullWidth
        loading={submitting}
        disabled={!acknowledged || submitting}
        onClick={handleDelete}
      >
        Delete account
      </Button>
    </div>
  );
};
